import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import { VISIT_SYNC_BATCH_SIZE } from '../shared/constants.js';
import { ApiClientError, apiClient } from './api.js';
import type { Location, Paginated, PendingVisit, VisitInput } from './types.js';

interface FlyerOpsDatabase extends DBSchema {
  pendingVisits: {
    key: string;
    value: PendingVisit;
    indexes: { 'by-state': PendingVisit['state']; 'by-updated': string };
  };
  responseCache: {
    key: string;
    value: { key: string; payload: unknown; cachedAt: string };
  };
  offlineMetadata: {
    key: string;
    value: { key: string; userId: string; updatedAt: string };
  };
}

let databasePromise: Promise<IDBPDatabase<FlyerOpsDatabase>> | null = null;
let offlineDataGeneration = 0;
const staleSyncAfterMs = 60_000;
const maxSyncRequestsPerDrain = 100;

function database(): Promise<IDBPDatabase<FlyerOpsDatabase>> {
  databasePromise ??= openDB<FlyerOpsDatabase>('campus-flyer-operations', 2, {
    upgrade(db, oldVersion) {
      if (oldVersion < 1) {
        const visits = db.createObjectStore('pendingVisits', {
          keyPath: 'idempotencyKey',
        });
        visits.createIndex('by-state', 'state');
        visits.createIndex('by-updated', 'updatedAt');
        db.createObjectStore('responseCache', { keyPath: 'key' });
      }
      if (oldVersion < 2) {
        db.createObjectStore('offlineMetadata', { keyPath: 'key' });
      }
    },
  });
  return databasePromise;
}

export async function prepareOfflineDataForUser(
  userId: string,
): Promise<{ clearedPreviousOwnerData: boolean }> {
  const db = await database();
  const transaction = db.transaction(
    ['pendingVisits', 'responseCache', 'offlineMetadata'],
    'readwrite',
  );
  const [owner, pendingRecords, cachedResponses] = await Promise.all([
    transaction.objectStore('offlineMetadata').get('owner'),
    transaction.objectStore('pendingVisits').getAll(),
    transaction.objectStore('responseCache').count(),
  ]);
  const hasIncompatibleRecord = pendingRecords.some((record) => record.ownerUserId !== userId);
  const shouldClear =
    (owner !== undefined && owner.userId !== userId) ||
    (owner === undefined && (pendingRecords.length > 0 || cachedResponses > 0)) ||
    hasIncompatibleRecord;
  if (shouldClear) {
    offlineDataGeneration += 1;
    await Promise.all([
      transaction.objectStore('pendingVisits').clear(),
      transaction.objectStore('responseCache').clear(),
    ]);
  }
  await transaction.objectStore('offlineMetadata').put({
    key: 'owner',
    userId,
    updatedAt: new Date().toISOString(),
  });
  await transaction.done;
  return { clearedPreviousOwnerData: shouldClear };
}

export async function queueVisit(event: VisitInput, ownerUserId: string): Promise<PendingVisit> {
  const generation = offlineDataGeneration;
  const now = new Date().toISOString();
  const pending: PendingVisit = {
    idempotencyKey: event.idempotencyKey,
    ownerUserId,
    event: { ...event, source: 'offline_sync' },
    state: 'pending',
    queuedAt: now,
    updatedAt: now,
  };
  const db = await database();
  const transaction = db.transaction(['pendingVisits', 'offlineMetadata'], 'readwrite');
  const owner = await transaction.objectStore('offlineMetadata').get('owner');
  if (generation !== offlineDataGeneration || owner?.userId !== ownerUserId) {
    await transaction.done;
    throw new Error('The offline queue was cleared during sign-out.');
  }
  await transaction.objectStore('pendingVisits').put(pending);
  await transaction.done;
  return pending;
}

export function isPendingVisit(record: PendingVisit): boolean {
  return record.state === 'pending' || record.state === 'syncing';
}

export async function clearOfflineData(): Promise<void> {
  offlineDataGeneration += 1;
  const db = await database();
  const transaction = db.transaction(
    ['pendingVisits', 'responseCache', 'offlineMetadata'],
    'readwrite',
  );
  await Promise.all([
    transaction.objectStore('pendingVisits').clear(),
    transaction.objectStore('responseCache').clear(),
    transaction.objectStore('offlineMetadata').delete('owner'),
    transaction.done,
  ]);
}

export async function visitQueue(): Promise<PendingVisit[]> {
  const records = await (await database()).getAll('pendingVisits');
  return records.sort((left, right) => left.queuedAt.localeCompare(right.queuedAt));
}

export async function retryVisit(idempotencyKey: string, ownerUserId: string): Promise<void> {
  const db = await database();
  const transaction = db.transaction(['pendingVisits', 'offlineMetadata'], 'readwrite');
  const [existing, owner] = await Promise.all([
    transaction.objectStore('pendingVisits').get(idempotencyKey),
    transaction.objectStore('offlineMetadata').get('owner'),
  ]);
  if (!existing || owner?.userId !== ownerUserId || existing.ownerUserId !== ownerUserId) {
    await transaction.done;
    return;
  }
  const withoutError = { ...existing };
  delete withoutError.error;
  await transaction.objectStore('pendingVisits').put({
    ...withoutError,
    state: 'pending',
    updatedAt: new Date().toISOString(),
  });
  await transaction.done;
}

interface SyncResult {
  created: number;
  duplicated: number;
  failed: number;
}

interface SyncBatchResult extends SyncResult {
  attemptedKeys: string[];
  stopDrain: boolean;
}

async function syncVisitQueueBatch(
  ownerUserId: string,
  alreadyAttempted: ReadonlySet<string>,
): Promise<SyncBatchResult> {
  const generation = offlineDataGeneration;
  const db = await database();
  const staleBefore = Date.now() - staleSyncAfterMs;
  const claim = db.transaction(['pendingVisits', 'offlineMetadata'], 'readwrite');
  const [owner, queuedRecords] = await Promise.all([
    claim.objectStore('offlineMetadata').get('owner'),
    claim.objectStore('pendingVisits').getAll(),
  ]);
  if (generation !== offlineDataGeneration || owner?.userId !== ownerUserId) {
    await claim.done;
    return {
      created: 0,
      duplicated: 0,
      failed: 0,
      attemptedKeys: [],
      stopDrain: true,
    };
  }
  const ownedRecords = queuedRecords.filter((record) => record.ownerUserId === ownerUserId);
  const previousByKey = new Map(
    ownedRecords.map((record) => [record.idempotencyKey, record] as const),
  );
  const normalizedRecords = ownedRecords.map((record) => {
    if (record.state !== 'syncing') return record;
    const updatedAt = Date.parse(record.updatedAt);
    if (Number.isFinite(updatedAt) && updatedAt > staleBefore) return record;
    return {
      ...record,
      state: 'pending' as const,
      updatedAt: new Date().toISOString(),
    };
  });
  const recovered = normalizedRecords.filter((record) => {
    const previous = previousByKey.get(record.idempotencyKey);
    return previous?.state === 'syncing' && record.state === 'pending';
  });
  await Promise.all(recovered.map((record) => claim.objectStore('pendingVisits').put(record)));
  const records = normalizedRecords
    .filter((record) => record.state === 'pending' && !alreadyAttempted.has(record.idempotencyKey))
    .sort((left, right) => left.queuedAt.localeCompare(right.queuedAt))
    .slice(0, VISIT_SYNC_BATCH_SIZE);
  if (records.length === 0) {
    await claim.done;
    return {
      created: 0,
      duplicated: 0,
      failed: 0,
      attemptedKeys: [],
      stopDrain: true,
    };
  }
  const attemptedKeys = records.map((record) => record.idempotencyKey);
  const now = new Date().toISOString();
  await Promise.all(
    records.map((record) =>
      claim.objectStore('pendingVisits').put({
        ...record,
        state: 'syncing',
        updatedAt: now,
      }),
    ),
  );
  await claim.done;
  if (generation !== offlineDataGeneration) {
    return {
      created: 0,
      duplicated: 0,
      failed: 0,
      attemptedKeys,
      stopDrain: true,
    };
  }
  try {
    const response = await apiClient.syncVisits(
      records.map((record) => record.event),
      ownerUserId,
    );
    if (generation !== offlineDataGeneration) {
      return {
        created: 0,
        duplicated: 0,
        failed: 0,
        attemptedKeys,
        stopDrain: true,
      };
    }
    const finalize = db.transaction(['pendingVisits', 'offlineMetadata'], 'readwrite');
    const currentOwner = await finalize.objectStore('offlineMetadata').get('owner');
    if (currentOwner?.userId !== ownerUserId) {
      await finalize.done;
      return {
        created: 0,
        duplicated: 0,
        failed: 0,
        attemptedKeys,
        stopDrain: true,
      };
    }
    let created = 0;
    let duplicated = 0;
    let failed = 0;
    const reportedKeys = new Set<string>();
    const recordsByKey = new Map(records.map((record) => [record.idempotencyKey, record]));
    const operations: Array<Promise<unknown>> = [];
    for (const result of response.data.results) {
      const indexedRecord =
        result.clientIndex !== undefined ? records[result.clientIndex] : undefined;
      const record =
        indexedRecord &&
        (result.idempotencyKey === null || result.idempotencyKey === indexedRecord.idempotencyKey)
          ? indexedRecord
          : result.idempotencyKey
            ? recordsByKey.get(result.idempotencyKey)
            : undefined;
      if (!record) continue;
      reportedKeys.add(record.idempotencyKey);
      if (result.status === 'failed') {
        if (result.error.retryable) {
          operations.push(
            finalize.objectStore('pendingVisits').put({
              ...record,
              state: 'pending',
              error: result.error,
              updatedAt: new Date().toISOString(),
            }),
          );
        } else {
          failed += 1;
          operations.push(
            finalize.objectStore('pendingVisits').put({
              ...record,
              state: 'failed',
              error: result.error,
              updatedAt: new Date().toISOString(),
            }),
          );
        }
      } else {
        if (result.status === 'created') created += 1;
        else duplicated += 1;
        operations.push(finalize.objectStore('pendingVisits').delete(record.idempotencyKey));
      }
    }
    operations.push(
      ...records
        .filter((record) => !reportedKeys.has(record.idempotencyKey))
        .map((record) =>
          finalize.objectStore('pendingVisits').put({
            ...record,
            state: 'pending',
            error: {
              code: 'sync_response_incomplete',
              message: 'The server did not confirm this update. It will be retried.',
            },
            updatedAt: new Date().toISOString(),
          }),
        ),
    );
    await Promise.all(operations);
    await finalize.done;
    return { created, duplicated, failed, attemptedKeys, stopDrain: false };
  } catch (error) {
    if (generation !== offlineDataGeneration) {
      return {
        created: 0,
        duplicated: 0,
        failed: 0,
        attemptedKeys,
        stopDrain: true,
      };
    }
    const retryable =
      !(error instanceof ApiClientError) ||
      error.status >= 500 ||
      error.status === 429 ||
      error.status === 401 ||
      (error.status === 403 && error.code === 'csrf_failed');
    const finalize = db.transaction(['pendingVisits', 'offlineMetadata'], 'readwrite');
    const currentOwner = await finalize.objectStore('offlineMetadata').get('owner');
    if (currentOwner?.userId !== ownerUserId) {
      await finalize.done;
      return {
        created: 0,
        duplicated: 0,
        failed: 0,
        attemptedKeys,
        stopDrain: true,
      };
    }
    await Promise.all(
      records.map((record) => {
        const withoutError = { ...record };
        delete withoutError.error;
        return finalize.objectStore('pendingVisits').put(
          retryable
            ? {
                ...withoutError,
                state: 'pending',
                updatedAt: new Date().toISOString(),
              }
            : {
                ...withoutError,
                state: 'failed',
                error: {
                  code: error instanceof ApiClientError ? error.code : 'sync_failed',
                  message:
                    error instanceof Error ? error.message : 'The update could not be synced.',
                },
                updatedAt: new Date().toISOString(),
              },
        );
      }),
    );
    await finalize.done;
    return {
      created: 0,
      duplicated: 0,
      failed: retryable ? 0 : records.length,
      attemptedKeys,
      stopDrain: true,
    };
  }
}

export async function syncVisitQueue(ownerUserId: string): Promise<SyncResult> {
  const totals: SyncResult = { created: 0, duplicated: 0, failed: 0 };
  if (!navigator.onLine) return totals;

  const attemptedKeys = new Set<string>();
  for (let requestCount = 0; requestCount < maxSyncRequestsPerDrain; requestCount += 1) {
    const batch = await syncVisitQueueBatch(ownerUserId, attemptedKeys);
    totals.created += batch.created;
    totals.duplicated += batch.duplicated;
    totals.failed += batch.failed;
    for (const key of batch.attemptedKeys) attemptedKeys.add(key);
    if (batch.stopDrain || batch.attemptedKeys.length === 0) break;
  }
  return totals;
}

export async function cachedLocations(
  cacheKey: string,
  ownerUserId: string,
  fetcher: () => Promise<Paginated<Location>>,
): Promise<Paginated<Location>> {
  const generation = offlineDataGeneration;
  const db = await database();
  try {
    const payload = await fetcher();
    if (generation === offlineDataGeneration) {
      const transaction = db.transaction(['responseCache', 'offlineMetadata'], 'readwrite');
      const owner = await transaction.objectStore('offlineMetadata').get('owner');
      if (owner?.userId === ownerUserId && generation === offlineDataGeneration) {
        await transaction.objectStore('responseCache').put({
          key: cacheKey,
          payload,
          cachedAt: new Date().toISOString(),
        });
      }
      await transaction.done;
    }
    return payload;
  } catch (error) {
    if (error instanceof ApiClientError && error.status < 500) throw error;
    const transaction = db.transaction(['responseCache', 'offlineMetadata'], 'readonly');
    const [cached, owner] = await Promise.all([
      transaction.objectStore('responseCache').get(cacheKey),
      transaction.objectStore('offlineMetadata').get('owner'),
    ]);
    await transaction.done;
    if (!cached || owner?.userId !== ownerUserId || generation !== offlineDataGeneration) {
      throw error;
    }
    return cached.payload as Paginated<Location>;
  }
}
